import React, { useEffect, useState } from "react";
import { api, formatApiError, formatDateFR } from "../../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, X, Copy, KeyRound, UserX, UserCheck, Trash2, Crown } from "lucide-react";

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-violet-600 outline-none";
const labelCls = "text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", job_title: "", location_id: "", role_id: "", restrict_to_location: false };

export default function TeamSection() {
  const [members, setMembers] = useState(null);
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [tempPwd, setTempPwd] = useState(null); // { email, password }
  const [forbidden, setForbidden] = useState(false);

  const load = () =>
    api.get("/vendor/team/members")
      .then((r) => setMembers(r.data.members || []))
      .catch((e) => { if (e.response?.status === 403) setForbidden(true); setMembers([]); });

  useEffect(() => {
    load();
    api.get("/vendor/team/roles").then((r) => setRoles(r.data.roles || [])).catch(() => {});
    api.get("/vendor/locations").then((r) => setLocations(r.data.locations || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.role_id) { toast.error("Sélectionnez un rôle"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/vendor/team/members", { ...form, location_id: form.location_id || null });
      setTempPwd({ email: data.member.email, password: data.temp_password });
      toast.success(`${data.member.name} ajouté à l'équipe`);
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };

  const patch = async (m, payload, msg) => {
    try {
      await api.patch(`/vendor/team/members/${m.id}`, payload);
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const resetPwd = async (m) => {
    if (!window.confirm(`Générer un nouveau mot de passe provisoire pour ${m.name} ?`)) return;
    try {
      const { data } = await api.post(`/vendor/team/members/${m.id}/reset-password`);
      setTempPwd({ email: m.email, password: data.temp_password });
      toast.success("Mot de passe réinitialisé");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Supprimer ${m.name} ? (suppression douce : l'historique est conservé)`)) return;
    try {
      await api.delete(`/vendor/team/members/${m.id}`);
      toast.success("Utilisateur supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  if (forbidden) return <Forbidden />;
  if (members === null) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-violet-600" /></div>;

  return (
    <div className="space-y-4" data-testid="team-section">
      {tempPwd && (
        <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-4" data-testid="team-temp-password">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-emerald-800">🔑 Mot de passe provisoire pour {tempPwd.email}</div>
              <div className="mt-1 flex items-center gap-2">
                <code className="px-3 py-1.5 rounded-lg bg-white border border-emerald-200 text-sm font-mono font-bold text-slate-900" data-testid="team-temp-password-value">{tempPwd.password}</code>
                <button
                  onClick={() => { navigator.clipboard?.writeText(tempPwd.password); toast.success("Copié"); }}
                  className="h-8 px-2.5 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-100 inline-flex items-center gap-1 text-xs font-semibold"
                  data-testid="team-temp-password-copy"
                >
                  <Copy className="h-3 w-3" /> Copier
                </button>
              </div>
              <div className="text-[11px] text-emerald-700 mt-1.5">Communiquez-le à votre collaborateur — il ne sera plus jamais affiché. Il devra le changer dans Paramètres → Sécurité.</div>
            </div>
            <button onClick={() => setTempPwd(null)} className="text-emerald-600 hover:text-emerald-800"><X className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">{members.length} membre{members.length > 1 ? "s" : ""} dans l'équipe</div>
        <button onClick={() => setShowForm(!showForm)} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700" data-testid="team-add-member-btn">
          {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {showForm ? "Fermer" : "Inviter un collaborateur"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="rounded-2xl border border-violet-200 bg-violet-50/30 p-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="team-member-form">
          <div><label className={labelCls}>Prénom *</label><input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inputCls + " mt-1"} data-testid="team-first-name" /></div>
          <div><label className={labelCls}>Nom *</label><input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={inputCls + " mt-1"} data-testid="team-last-name" /></div>
          <div><label className={labelCls}>Email *</label><input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls + " mt-1"} data-testid="team-email" /></div>
          <div><label className={labelCls}>Téléphone</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls + " mt-1"} data-testid="team-phone" /></div>
          <div><label className={labelCls}>Fonction</label><input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder="Agent de comptoir…" className={inputCls + " mt-1"} data-testid="team-job" /></div>
          <div>
            <label className={labelCls}>Agence de rattachement</label>
            <select value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })} className={inputCls + " mt-1"} data-testid="team-location">
              <option value="">— Aucune —</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            {form.location_id && (
              <label className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                <input type="checkbox" checked={form.restrict_to_location} onChange={(e) => setForm({ ...form, restrict_to_location: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-600" data-testid="team-restrict-location" />
                Restreindre aux réservations de son agence
              </label>
            )}
          </div>
          <div>
            <label className={labelCls}>Rôle *</label>
            <select required value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })} className={inputCls + " mt-1"} data-testid="team-role">
              <option value="">Sélectionner…</option>
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div className="flex items-end sm:col-span-2 lg:col-span-2 justify-end">
            <button type="submit" disabled={saving} className="h-[38px] px-6 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-2" data-testid="team-member-submit">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Créer le compte (mot de passe provisoire)
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Utilisateur</th>
                <th className="text-left px-5 py-3 font-semibold">Fonction</th>
                <th className="text-left px-5 py-3 font-semibold">Agence</th>
                <th className="text-left px-5 py-3 font-semibold">Rôle</th>
                <th className="text-left px-5 py-3 font-semibold">Statut</th>
                <th className="text-right px-5 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {members.map((m) => (
                <tr key={m.id} className="hover:bg-slate-50/60" data-testid={`team-member-row-${m.email}`}>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      {m.is_owner && <Crown className="h-3.5 w-3.5 text-amber-500 shrink-0" title="Propriétaire" />}
                      <div>
                        <div className="font-semibold text-slate-900">{m.name}</div>
                        <div className="text-[11px] text-slate-500">{m.email}{m.phone ? ` · ${m.phone}` : ""}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-slate-600 text-xs">{m.job_title || "—"}</td>
                  <td className="px-5 py-3.5 text-slate-600 text-xs">
                    {locations.find((l) => l.id === m.location_id)?.name || "—"}
                    {m.location_id && !m.is_owner && (
                      <button
                        onClick={() => patch(m, { restrict_to_location: !m.restrict_to_location }, m.restrict_to_location ? "Scope agence désactivé" : "Scope agence activé")}
                        className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase transition-colors ${m.restrict_to_location ? "bg-sky-100 text-sky-700 hover:bg-sky-200" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`}
                        title={m.restrict_to_location ? "Ne voit que les réservations de son agence — cliquer pour désactiver" : "Voit toutes les réservations — cliquer pour restreindre à son agence"}
                        data-testid={`team-restricted-badge-${m.id}`}
                      >
                        {m.restrict_to_location ? "Scope agence ✓" : "Scope agence"}
                      </button>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    {m.is_owner ? (
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">Propriétaire</span>
                    ) : (
                      <select
                        value={m.role_id || ""}
                        onChange={(e) => patch(m, { role_id: e.target.value }, "Rôle mis à jour")}
                        className="rounded-lg border border-slate-200 px-2 py-1 text-xs focus:ring-2 focus:ring-violet-600 outline-none"
                        data-testid={`team-role-select-${m.email}`}
                      >
                        {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${m.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid={`team-status-${m.email}`}>
                      {m.status === "active" ? "Actif" : "Désactivé"}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    {!m.is_owner && (
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => resetPwd(m)} className="h-7 w-7 rounded-md hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center" title="Réinitialiser le mot de passe" data-testid={`team-reset-pwd-${m.email}`}>
                          <KeyRound className="h-3.5 w-3.5" />
                        </button>
                        {m.status === "active" ? (
                          <button onClick={() => patch(m, { status: "disabled" }, "Utilisateur désactivé")} className="h-7 w-7 rounded-md hover:bg-amber-50 text-amber-600 inline-flex items-center justify-center" title="Désactiver" data-testid={`team-disable-${m.email}`}>
                            <UserX className="h-3.5 w-3.5" />
                          </button>
                        ) : (
                          <button onClick={() => patch(m, { status: "active" }, "Utilisateur réactivé")} className="h-7 w-7 rounded-md hover:bg-emerald-50 text-emerald-600 inline-flex items-center justify-center" title="Réactiver" data-testid={`team-enable-${m.email}`}>
                            <UserCheck className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button onClick={() => remove(m)} className="h-7 w-7 rounded-md hover:bg-red-50 text-red-500 inline-flex items-center justify-center" title="Supprimer" data-testid={`team-delete-${m.email}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function Forbidden() {
  return (
    <div className="py-16 text-center rounded-2xl border border-dashed border-slate-200" data-testid="team-forbidden">
      <div className="text-2xl mb-2">🔒</div>
      <div className="text-sm text-slate-500">Vous n'avez pas la permission d'accéder à cette section.</div>
    </div>
  );
}
